import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Alert, Button, Chip, CircularProgress, Collapse, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import DocumentShareLinkActionsMenu from "components/DocumentShareLink/DocumentShareLinkActionsMenu";
import {
  fetchDocumentShareLinks,
  regenerateDocumentShareLink,
  setDocumentShareLinkRevoked,
} from "services/admissionDocumentEmailApi";
import { formatDisplayDate } from "utils/dateFormat";

const DOCUMENT_LINKS_ADMIN_PATH = "/admin/document-links";

const PermanentLinksManager = ({ studentId }) => {
  const navigate = useNavigate();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(() => {
    if (!studentId) return;
    setLoading(true);
    fetchDocumentShareLinks(studentId)
      .then((list) => setLinks(Array.isArray(list) ? list : []))
      .catch(() => toast.error("Failed to load existing permanent links."))
      .finally(() => setLoading(false));
  }, [studentId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-1">
        <CircularProgress size={14} />
        <Typography variant="caption">Loading existing permanent links…</Typography>
      </div>
    );
  }
  if (!links.length) return null;

  return (
    <div className="rounded border border-app-border">
      <div className="flex w-full items-center gap-1 px-2 py-1.5">
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          className="flex min-w-0 flex-1 items-center text-left"
          aria-expanded={expanded}
        >
          <Typography variant="caption" className="truncate font-semibold text-app-text">
            Existing permanent links ({links.length})
          </Typography>
        </button>
        {expanded ? (
          <Button
            variant="outlined"
            size="small"
            color="primary"
            onClick={() => navigate(DOCUMENT_LINKS_ADMIN_PATH)}
            sx={{
              flexShrink: 0,
              fontSize: "0.7rem",
              py: 0.25,
              px: 1,
              textTransform: "none",
            }}
          >
            View all document links
          </Button>
        ) : null}
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          className="shrink-0 text-app-text-muted"
          aria-label={expanded ? "Collapse permanent links" : "Expand permanent links"}
        >
          <ExpandMoreIcon
            fontSize="small"
            className="transition-transform"
            style={{ transform: expanded ? "rotate(180deg)" : "none" }}
          />
        </button>
      </div>
      <Collapse in={expanded} unmountOnExit>
        <div className="max-h-36 overflow-y-auto border-t border-app-border">
          {links.map((link) => {
            const dead = !link.documentPresent;
            return (
              <div
                key={link.id}
                className="flex items-center gap-2 px-2 py-1 border-b border-app-border last:border-b-0"
              >
                <span
                  className={`text-sm truncate flex-1 ${
                    link.revoked || dead ? "text-app-text-muted line-through" : "text-app-text"
                  }`}
                >
                  {link.documentName || "Document"}
                </span>
                <Typography variant="caption" color="text.secondary" className="shrink-0">
                  {formatDisplayDate(link.createdDate, "")}
                </Typography>
                {dead && (
                  <Chip
                    size="small"
                    label="Document removed"
                    color="warning"
                    sx={{ height: 18, fontSize: 10 }}
                  />
                )}
                {link.permanentlyDisabled && (
                  <Chip
                    size="small"
                    label="Disabled"
                    color="error"
                    sx={{ height: 18, fontSize: 10 }}
                  />
                )}
                {!link.permanentlyDisabled && link.revoked && (
                  <Chip
                    size="small"
                    label="Revoked"
                    color="default"
                    sx={{ height: 18, fontSize: 10 }}
                  />
                )}
                {link.supersededByTokenId && !link.permanentlyDisabled && !link.revoked && (
                  <Chip
                    size="small"
                    label="Superseded"
                    color="info"
                    sx={{ height: 18, fontSize: 10 }}
                  />
                )}
                <DocumentShareLinkActionsMenu
                  link={link}
                  busy={busyId}
                  onBusyChange={setBusyId}
                  onRefresh={load}
                  setRevoked={(tokenId, revoked) =>
                    setDocumentShareLinkRevoked(studentId, tokenId, revoked)
                  }
                  regenerate={(tokenId, revokeOld) =>
                    regenerateDocumentShareLink(studentId, tokenId, revokeOld)
                  }
                />
              </div>
            );
          })}
        </div>
        <Alert severity="info" sx={{ borderRadius: 0 }} className="!py-0">
          Sending again reuses (and re-enables) the link for the same file unless it was permanently
          disabled.
        </Alert>
      </Collapse>
    </div>
  );
};

export default PermanentLinksManager;
